/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import containerPaths from './container-paths';
import { dedicatedEndpointsPaths } from './dedicated-endpoints-paths';
import { defaultPaths } from './default-paths';
import serverlessEndpointsPaths from './serverless-endpoints-paths';
import { teamSettingsPaths } from './team-settings-paths';
import { userSettingsPaths } from './user-settings-paths';

// Login
const loginPaths = (base: string) => ({
  ...defaultPaths(base),
  cli: `${base}/cli`,
  invite: `${base}/invite`,
});

// Signup
const signupPaths = (base: string) => ({
  ...defaultPaths(base),
  selfRegister: `${base}/self-register`,
  checkInbox: `${base}/check-inbox`,
  invite: `${base}/invite`,
  verification: `${base}/verification`,
});

// Get started
const getStartedPaths = (base: string) => ({
  ...defaultPaths(base),
  suite: `${base}/suite`,
  serverlessEndpoints: `${base}/serverless-endpoints`,
  dedicatedEndpoints: `${base}/dedicated-endpoints`,
  container: `${base}/container`,
});

// Check authority
const checkAuthorityPaths = (base: string) => ({
  ...defaultPaths(base),
  serverlessEndpoints: `${base}/serverless-endpoints`,
  container: `${base}/container`,
});

const generalPaths = (base: string) => ({
  ...defaultPaths(base),
  profile: `${base}/profile`,
  productSelect: `${base}/product-select`,
  dedicatedEndpointsChoosePlan: `${base}/dedicated-endpoints/choose-plan`,
});

// Steps order: profile
const serverlessEndpointsOnboardingStepPaths = (base: string) => ({
  serverlessEndpointsProfile: `${base}/profile`,
});

// Steps order: profile
const containerOnboardingStepPaths = (base: string) => ({
  profile: `${base}/profile`,
});

// Steps order: profile -> choose plan
const dedicatedEndpointsOnboardingStepPaths = (base: string) => ({
  profile: `${base}/profile`,
  choosePlan: `${base}/choose-plan`,
});

const onboardingPaths = (base: string) => ({
  ...defaultPaths(base),
  invite: `${base}/invite`,
  general: generalPaths(`${base}/general`),

  // Product scoped
  serverlessEndpoints: serverlessEndpointsOnboardingStepPaths(`${base}/serverless-endpoints`),
  container: containerOnboardingStepPaths(`${base}/container`),
  dedicatedEndpoints: dedicatedEndpointsOnboardingStepPaths(`${base}/dedicated-endpoints`),
});

// Team
const teamPaths = (base: string) => ({
  ...defaultPaths(base),
  suite: `${base}/suite`,
  serverlessEndpoints: serverlessEndpointsPaths(`${base}/serverless-endpoints`),
  dedicatedEndpoints: dedicatedEndpointsPaths(`${base}/dedicated-endpoints`),
  container: containerPaths(`${base}/container`),
  checkAuthority: checkAuthorityPaths(`${base}/check-authority`),
  teamSettings: teamSettingsPaths(base),
});

// Main Paths
const Paths = {
  ...defaultPaths(`/`),

  // Error
  error: `/error`,

  sso: `/sso`,
  enterCode: `/enter-code`,

  // Auth
  login: loginPaths(`/login`),
  signup: signupPaths(`/signup`),
  forgotPassword: `/forgot-password`,
  resetPassword: `/reset-password`,

  invite: `/invite`,
  onboarding: onboardingPaths(`/onboarding`),
  onboardingInvite: `/onboarding/invite`,

  getStarted: getStartedPaths(`/get-started`),

  userSettings: userSettingsPaths(`/user-settings`),

  // Start from team id
  defaultTeam: teamPaths(`/default-team`),
  team: (id: string) => teamPaths(`/team/${id}`),
};

export default Paths;
